const LANDING_BLOG_POST_LIMIT = 3;
export const LANDING_BLOG_CANDIDATE_LIMIT = 30;

const PRIORITY_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;
const PRODUCT_UPDATE_TAGS = new Set(['platform update', 'product update']);

type TaggedBlogPost = {
  date?: string | null;
  tags?: string | null;
};

const normalizeTag = (value: string) =>
  value
    .trim()
    .toLocaleLowerCase('en')
    .replaceAll(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replaceAll(/\s+/g, ' ');

const tagsOf = (post: TaggedBlogPost) =>
  (post.tags ?? '').split(',').map(normalizeTag).filter(Boolean);

const isFresh = (post: TaggedBlogPost, now: number) => {
  if (!post.date) return false;
  const publishedAt = Date.parse(post.date);
  const age = now - publishedAt;
  return Number.isFinite(publishedAt) && age >= 0 && age <= PRIORITY_MAX_AGE_MS;
};

const isCurrentGameTag = (tag: string, gameName: string) => {
  if (tag === gameName) return true;

  // Landing names sometimes carry a suffix that editorial tags intentionally omit:
  // "Battlefield 6 & RedSec" -> "Battlefield 6", "Warzone 2.0" -> "Warzone".
  return tag.length >= 4 && gameName.startsWith(`${tag} `);
};

/**
 * Game landings reserve the first two relevant slots for at most one current-game article
 * and one Teamplay product update, then fill the remainder from the chronological feed.
 * The homepage stays a plain chronological feed because it has no game context.
 */
export const prioritizeLandingBlogPosts = <Post extends TaggedBlogPost>(
  posts: Post[],
  gameName?: string,
  now = Date.now()
) => {
  if (!gameName) return posts.slice(0, LANDING_BLOG_POST_LIMIT);

  const normalizedGameName = normalizeTag(gameName);
  const currentGamePost = posts.find(
    (post) =>
      isFresh(post, now) && tagsOf(post).some((tag) => isCurrentGameTag(tag, normalizedGameName))
  );
  const productUpdatePost = posts.find(
    (post) =>
      post !== currentGamePost &&
      isFresh(post, now) &&
      tagsOf(post).some((tag) => PRODUCT_UPDATE_TAGS.has(tag))
  );
  const prioritized = [currentGamePost, productUpdatePost].filter(
    (post): post is Post => post !== undefined
  );

  for (const post of posts) {
    if (prioritized.length >= LANDING_BLOG_POST_LIMIT) break;
    if (!prioritized.includes(post)) prioritized.push(post);
  }

  return prioritized;
};
